import { useBasketStore } from '@/store/basket/basket'
import type { DeliveryDataAddress } from '@/store/delivery/delivery.types'
import { useToggleStore } from '@/store/toggle/toggle'

import { SetDeliveryMethodV5Payload } from '@/api/modules/basket/types'
import type { ProductV5 } from '@/types/api/api-v5/product'
import type { OneProductV5 } from '@/types/api/api-v5/product'

import type { SetDeliveryMethodPayload } from '@/api/modules/basket/types'
import type { MenuByPointPayload } from '@/api/modules/category/types'
import { storage } from '@/services/storage'
import { STORAGE_KEY } from '@/services/storage/constants'

export const useMenuByPoint = () => {
  const basketStore = useBasketStore()
  const { menuByPointsEnabled } = useToggleStore()

  const menuByPointData = computed(() => {
    let fetchMainSlidersV5Payload: MenuByPointPayload = {}

    if (basketStore.isLoaded) {
      const deliveryMethod = basketStore.data.delivery?.method
      const pointId = basketStore.data.delivery?.pointId

      fetchMainSlidersV5Payload = {
        ...(deliveryMethod && pointId && { delivery_method: deliveryMethod }),
        ...(deliveryMethod && pointId && { point_id: pointId }),
      }
    }

    return fetchMainSlidersV5Payload
  })

  const getAsyncDataKeyComputed = (asyncDataKey: string) => {
    // Ключ для fetch данных меню по точке на основе выбранного адреса
    return computed(() => {
      if (!menuByPointsEnabled) return asyncDataKey

      let key = `${asyncDataKey}`

      if (menuByPointData.value.delivery_method) {
        key = `${key}-${menuByPointData.value.delivery_method}`
      }

      if (menuByPointData.value.point_id) {
        key = `${key}-${menuByPointData.value.point_id}`
      }

      return key
    })
  }

  const getAvailableProductsData = (product: ProductV5 | OneProductV5) => {
    // Если блюдо (первая модификация) недоступно - весь продукт недоступен
    const isAvailable = product.modifications[0]?.available_for_order

    // Оставляем только доступные модификации
    const modifications = product.modifications.filter(
      (modification) => modification.available_for_order,
    )

    return {
      isAvailable: isAvailable,
      modifications: modifications,
    }
  }

  // Выбран ли адрес у пользователя
  const isAddressV5Selected = computed(() =>
    Boolean(
      menuByPointData.value.point_id && menuByPointData.value.delivery_method,
    ),
  )

  // Синхронизируем адрес с V5 API меню
  const syncAddressWithV5Api = async () => {
    if (isAddressV5Selected.value) return

    const addressId =
      basketStore.data.delivery?.addresses[basketStore.data.delivery?.method]

    const isAddressV4Selected = basketStore.data.delivery?.method && addressId

    // Если нет v5 адреса, но есть v4 адрес - синхронизируем его
    if (isAddressV4Selected) {
      const payload: SetDeliveryMethodV5Payload = {
        delivery_method: basketStore.data.delivery?.method,
        address_id: addressId as number,
      }

      await basketStore.setBasketDeliveryV5(payload)

      return
    }

    const deliveryWidget = storage.get<SetDeliveryMethodPayload>(
      STORAGE_KEY.addressSystem.deliveryWidget,
    )
    const deliveryAddress = storage.get<DeliveryDataAddress>(
      STORAGE_KEY.addressSystem.deliveryAddress,
    )

    // Если нет v5 адреса и v4, но есть адрес в localStorage - синхронизируем его
    if (deliveryWidget?.order_address) {
      const payload: SetDeliveryMethodV5Payload = {
        delivery_method: deliveryWidget.delivery_method,
        point_id: deliveryWidget?.point_id,
        address_id: deliveryWidget?.address_id,
        ...(deliveryAddress?.coordinates && {
          coordinates: deliveryAddress?.coordinates,
        }),
      }

      await basketStore.setBasketDeliveryV5(payload)
    }
  }

  return {
    menuByPointData,
    isAddressV5Selected,
    getAsyncDataKeyComputed,
    getAvailableProductsData,
    syncAddressWithV5Api,
  }
}
